import { useEffect, useState, useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Grid,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import * as Yup from 'yup';
import { useForm, useFieldArray, Controller, useFormContext } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import { useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFAutocomplete, RHFTextField } from 'src/components/hook-form';
import { postRequest } from 'src/services';
import { endpoints } from 'src/utils/axios';
import { handleErrorMessages } from '../../risk-register/feching';
import Iconify from 'src/components/iconify';
import { display } from '@mui/system';
import { useParams } from 'react-router';

const ToolsCreate = ({
  open,
  onClose,
  reload,
  currentItem,
  createdBy,
  closeform,
  jobSiteAddress,
  officeAddress,
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const { id } = useParams();

  const NewUserSchema = Yup.object().shape({
    tools: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required('Name is required'),
        quantity: Yup.number().required('Quantity is required').min(1),
        unit: Yup.string().required('Unit is required'),
        unit_price: Yup.number().required('Unit Price is required').min(0),
        availability: Yup.string().required('Availability is required'),
        location_type: Yup.string().required('Location Type is required'),
        // location: Yup.string().required('Location is required'),
        // amount: Yup.number().required('Amount is required').min(0),
      })
    ),
  });

  const defaultValues = useMemo(
    () => ({
      tools: currentItem?.tools || [
        {
          name: currentItem?.name || '',
          quantity: currentItem?.quantity || null,
          unit: currentItem?.unit || '',
          unit_price: currentItem?.unit_price || null,
          availability:
            currentItem?.availability === 1
              ? 'Yes'
              : currentItem?.availability === 0
              ? 'No'
              : 'Yes',
          location_type: currentItem?.location_type || 'None',
          location: currentItem?.location || '',
          amount: currentItem?.amount || 0,
        },
      ],
    }),
    [currentItem]
  );

  const methods = useForm({
    resolver: yupResolver(NewUserSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { isSubmitting },
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'tools',
  });

  useEffect(() => {
    reset(defaultValues);
  }, [currentItem, reset, defaultValues]);

  const onSubmit = handleSubmit(async (data) => {
    let newData;
    if (currentItem?.id) {
      newData = data?.tools.map((tool) => ({
        ...tool,
        id: currentItem?.id,
        availability: tool.availability === 'No' ? 0 : 1,
        performed_by: tool.performed_by?.id,
        supplier_id: tool.supplier_id?.id,
        project_id: id,
      }));
    } else {
      newData = data?.tools.map((tool) => ({
        ...tool,
        availability: tool.availability === 'No' ? 0 : 1,
        performed_by: tool.performed_by?.id,
        supplier_id: tool.supplier_id?.id,
        project_id: id,
      }));
    }
    setLoading(true);
    const payload = {
      tools: newData,
    };

    try {
      const response = await postRequest(
        currentItem?.id
          ? `${endpoints.accounting.inventory.tools.update_list}`
          : endpoints.accounting.inventory.tools.post_list,
        payload,
        true
      );
      if (response.metadata && response.metadata.success) {
        reset();
        onClose();
        closeform();
        reload();
        enqueueSnackbar({
          variant: 'success',
          message: response.metadata.message,
        });
      } else {
        handleErrorMessages(response?.message || 'An error occurred.');
      }
    } catch (err) {
      console.error('Error:', err);
      handleErrorMessages(err.message || 'Submission failed.');
    } finally {
      setLoading(false);
    }
  });

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      fullWidth
      maxWidth="lg"
      sx={{
        borderRadius: 'none',
        padding: '60px',
        fontSize: '13px',
        fontFamily: 'arial',
      }}
      transitionDuration={{
        enter: theme.transitions.duration.shortest,
        exit: theme.transitions.duration.shortest - 80,
      }}
    >
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <span
            className="cardColor"
            style={{ padding: '7px', marginLeft: '-25px', color: 'white', fontWeight: '700' }}
          >
            &nbsp;&nbsp; {currentItem?.id ? 'Update Tools' : 'Create Tools'}{' '}
            &nbsp;&nbsp;&nbsp;&nbsp;
          </span>
          <IconButton onClick={onClose}>
            <Iconify icon="mdi:close" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ borderRadius: '1px' }}>
          {fields.map((field, index) => (
            <Box
              key={field.id}
              mt={2}
              display="grid"
              rowGap={3}
              columnGap={2}
              gridTemplateColumns="repeat(3, 1fr)"
            >
              <RHFTextField name={`tools.${index}.name`} label="Name" />
              <RHFTextField name={`tools.${index}.qr_and_barcode`} label="QR & Barcode Scanning" />
              <RHFTextField name={`tools.${index}.iot_details`} label="IOT Details" />
              <RHFAutocomplete
                name={`tools.${index}.supplier_id`}
                label="Supplier & Manufacturer"
                options={createdBy}
                getOptionLabel={(option) => option?.name}
              />
              <Controller
                name={`tools.${index}.quantity`}
                control={control}
                render={({ field }) => (
                  <RHFTextField
                    {...field}
                    type="number"
                    label="Quantity"
                    onChange={(e) => {
                      field.onChange(e);
                      const newQuantity = e.target.value;
                      const unit_price = watch(`tools.${index}.unit_price`);
                      setValue(`tools.${index}.amount`, newQuantity * unit_price || 0);
                    }}
                  />
                )}
              />
              <RHFTextField name={`tools.${index}.unit`} label="Unit" />
              <Controller
                name={`tools.${index}.unit_price`}
                control={control}
                render={({ field }) => (
                  <RHFTextField
                    {...field}
                    type="number"
                    label="Unit Price"
                    onChange={(e) => {
                      field.onChange(e);
                      const quantity = watch(`tools.${index}.quantity`);
                      const newPrice = e.target.value;
                      setValue(`tools.${index}.amount`, quantity * newPrice || 0);
                    }}
                  />
                )}
              />
              <RHFAutocomplete
                name={`tools.${index}.availability`}
                label="Availability"
                options={['Yes', 'No']}
                getOptionLabel={(option) => option}
              />
              <RHFAutocomplete
                name={`tools.${index}.performed_by`}
                label="Performed by"
                options={createdBy}
                getOptionLabel={(option) => option.name}
              />
              <RHFAutocomplete
                name={`tools.${index}.location_type`}
                label="Location Type"
                options={['None', 'Job Site', 'Office Address', 'Others']}
                onChange={(_, value) => {
                  setValue(`tools.${index}.location_type`, value);

                  if (value === 'Job Site') {
                    setValue(`tools.${index}.location`, jobSiteAddress);
                  } else if (value === 'Office Address') {
                    setValue(`tools.${index}.location`, officeAddress);
                  } else {
                    setValue(`tools.${index}.location`, '');
                  }
                }}
                getOptionLabel={(option) => option}
              />
              <Controller
                name={`tools.${index}.location_type`}
                render={() => {
                  const locationType = watch(`tools.${index}.location_type`);
                  const locationValue = watch(`tools.${index}.location`);
                  return (
                    <RHFTextField
                      name={`tools.${index}.location`}
                      label="Location"
                      value={
                        locationType === 'Job Site'
                          ? jobSiteAddress
                          : locationType === 'Office Address'
                          ? officeAddress
                          : locationValue || ''
                      }
                      disabled={locationType !== 'Others'}
                    />
                  );
                }}
              />
              <Controller
                name={`tools.${index}.amount`}
                render={() => {
                  return (
                    <RHFTextField
                      name={`tools.${index}.amount`}
                      label="Amount"
                      type="number"
                      disabled
                    />
                  );
                }}
              />
              <RHFTextField name={`tools.${index}.remarks`} label="Remarks" />
              {!currentItem?.id && (
                <Box display="flex" justifyContent="start" gap={2} mt={2}>
                  <Button
                    size="small"
                    color="primary"
                    startIcon={<Iconify icon="mingcute:add-line" />}
                    onClick={() =>
                      append({
                        name: '',
                        quantity: '',
                        unit: '',
                        unit_price: '',
                        availability: 'Yes',
                        location_type: 'None',
                        location: '',
                        amount: '',
                      })
                    }
                  >
                    Add Item
                  </Button>
                  {fields.length > 1 && (
                    <Button
                      size="small"
                      color="error"
                      startIcon={<Iconify icon="mdi:delete" />}
                      onClick={() => remove(index)}
                    >
                      Remove Item
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          ))}
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            className="dark-button"
            loading={isSubmitting}
          >
            {currentItem?.id ? 'Update' : 'Create'}
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
};

export default ToolsCreate;
